import axios from "axios";

import { EntityKind } from "models/entityKind";
import { PdenSourceEnum } from "models/pdenDataSourceSetting";

export interface SmartQiRequest {
  filterId: string;
  productField: string; // "Performance.Oil.PeakCalRate" or "Performance.Gas.PeakCalRate" etc
  pageSize?: number;
  pageIndex?: number;
  pdenSource?: PdenSourceEnum;
}

// The API returns data in this format:
// { "wellId": ["wellName", "value"], ... }
export interface WellPeakRateResponse {
  [wellId: string]: [string, string]; // [wellName, peakRateValue]
}

export type SmartQiResponse = WellPeakRateResponse;

/**
 * Fetches peak production rates for wells in the current filter
 *
 * @param filterId - Filter ID to fetch wells from
 * @param productField - Product field to fetch (e.g., "Performance.Oil.PeakCalRate")
 * @param pageSize - Number of records to fetch (defaults to 10000)
 * @param pageIndex - Page index for pagination (defaults to 0)
 * @param pdenSource - Production data source (defaults to PdenSourceEnum.Public)
 */
export async function fetchWellPeakRates(
  filterId: string,
  productField: string,
  pageSize: number,
  pageIndex: number,
  pdenSource: PdenSourceEnum = PdenSourceEnum.Public
): Promise<SmartQiResponse> {
  const response = await axios.post<SmartQiResponse>(
    `${process.env.REACT_APP_DATA_RS_ROOT}/api/v1/data`,
    {
      filterId,
      fields: ["Header.EntityName", productField],
      entityKind: EntityKind.Well,
      outputFormatType: "JSON"
    },
    {
      params: {
        pdenSource,
        pageSize,
        pageIndex,
        sortColumn: "",
        sortDirection: "asc",
        entityKind: EntityKind.Well
      }
    }
  );

  return response.data;
}

/**
 * Calculates the average peak rate from the response data
 * Filters out null/undefined/invalid values
 *
 * @param data - Response data object with wellId keys and [wellName, value] arrays
 * @param productField - Not used with new format, kept for backward compatibility
 * @returns Average peak rate or null if no valid data
 */
export function calculateAveragePeakRate(data: WellPeakRateResponse): number | null {
  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  // Each entry is [wellName, peakRateValue] where peakRateValue is a string
  const allValues = Object.values(data);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const parsedValues = allValues.map(([_, valueStr]) => {
    const parsed = parseFloat(valueStr);
    return parsed;
  });

  const validRates = parsedValues.filter(
    (rate): rate is number => !isNaN(rate) && rate > 0
  );

  if (validRates.length === 0) {
    return null;
  }

  const sum = validRates.reduce((acc, rate) => acc + rate, 0);
  const average = sum / validRates.length;
  return average;
}
